import { Controller } from "@hotwired/stimulus"

// Popover that pastes a predefined text fragment into the compose box.
// Picking never sends: the fragment lands at the caret and the operator still
// reviews it and clicks Enviar.
export default class extends Controller {
  static targets = ["panel", "search", "list", "item", "empty", "input"]

  connect() {
    this.activeIndex = -1

    this.onDocumentClick = (event) => {
      if (this.isOpen && !this.element.contains(event.target)) this.close()
    }
    this.onKeydown = (event) => {
      if (event.key === "Escape" && this.isOpen) {
        event.stopPropagation()
        this.close()
        this.inputTarget?.focus()
      }
    }

    document.addEventListener("click", this.onDocumentClick)
    document.addEventListener("keydown", this.onKeydown)
  }

  disconnect() {
    document.removeEventListener("click", this.onDocumentClick)
    document.removeEventListener("keydown", this.onKeydown)
  }

  get isOpen() {
    return this.hasPanelTarget && !this.panelTarget.classList.contains("hidden")
  }

  toggle(event) {
    event.preventDefault()
    this.isOpen ? this.close() : this.open()
  }

  open() {
    if (!this.hasPanelTarget) return
    this.panelTarget.classList.remove("hidden")
    if (this.hasSearchTarget) {
      this.searchTarget.value = ""
      this.searchTarget.focus()
    }
    this.filter()
  }

  close() {
    if (!this.hasPanelTarget) return
    this.panelTarget.classList.add("hidden")
    this.activeIndex = -1
  }

  // Typing "/" as the only character in the compose box opens the picker.
  slashTrigger() {
    if (!this.hasInputTarget || this.isOpen) return
    if (this.inputTarget.value === "/") {
      this.inputTarget.value = ""
      this.open()
    }
  }

  filter() {
    const term = (this.hasSearchTarget ? this.searchTarget.value : "").trim().toLowerCase()
    let visible = 0

    this.itemTargets.forEach((item) => {
      const matches = !term || (item.dataset.search || "").includes(term)
      item.classList.toggle("hidden", !matches)
      if (matches) visible++
    })

    if (this.hasEmptyTarget) this.emptyTarget.classList.toggle("hidden", visible > 0)
    this.setActive(0)
  }

  navigate(event) {
    if (event.key === "ArrowDown") {
      event.preventDefault()
      this.setActive(this.activeIndex + 1)
    } else if (event.key === "ArrowUp") {
      event.preventDefault()
      this.setActive(this.activeIndex - 1)
    } else if (event.key === "Enter") {
      event.preventDefault()
      const item = this.visibleItems()[this.activeIndex]
      if (item) this.paste(item.dataset.body || "")
    }
  }

  insert(event) {
    this.paste(event.currentTarget.dataset.body || "")
  }

  visibleItems() {
    return this.itemTargets.filter((item) => !item.classList.contains("hidden"))
  }

  setActive(index) {
    const visible = this.visibleItems()
    if (visible.length === 0) {
      this.activeIndex = -1
      return
    }

    this.activeIndex = Math.max(0, Math.min(index, visible.length - 1))
    this.itemTargets.forEach((item) => item.classList.remove("bg-orange-50"))
    const active = visible[this.activeIndex]
    active.classList.add("bg-orange-50")
    active.scrollIntoView({ block: "nearest" })
  }

  // Paste at the caret, keep whatever was already typed, and leave focus in the
  // box — sending stays a separate, deliberate click.
  paste(body) {
    this.close()
    if (!this.hasInputTarget || !body) return

    const input = this.inputTarget
    const start = input.selectionStart ?? input.value.length
    const end = input.selectionEnd ?? input.value.length
    const before = input.value.slice(0, start)
    const after = input.value.slice(end)
    const separator = before && !/[\s]$/.test(before) ? " " : ""
    const caret = (before + separator + body).length

    input.value = `${before}${separator}${body}${after}`
    input.focus()
    input.setSelectionRange(caret, caret)
    input.dispatchEvent(new Event("input", { bubbles: true }))
  }
}
